import { LocalStorageService } from "./local-storage-service";
import { ticketLatestActivityMsForClientRead } from "./ticket-recent-comment-preview";

const STORAGE_KEY_PREFIX = "cx-portal-read-tickets-";

function storageKey(contactId: number): string {
  return `${STORAGE_KEY_PREFIX}${contactId}`;
}

function readMap(contactId: number): Record<string, number> {
  if (contactId <= 0) {
    return {};
  }
  try {
    const raw = LocalStorageService.getItem(storageKey(contactId));
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    return parsed != null && typeof parsed === "object"
      ? (parsed as Record<string, number>)
      : {};
  } catch {
    return {};
  }
}

function writeMap(contactId: number, map: Record<string, number>): void {
  if (contactId <= 0) {
    return;
  }
  LocalStorageService.setItem(storageKey(contactId), JSON.stringify(map));
}

/** UTC millis fingerprint of latest activity when the contact opened the thread. */
export function ticketActivityMsForClientRead(ticket: {
  recentComment?: { createdOn?: string } | null;
  recentCommentId?: string | null;
  lastModifiedJson?: string | null;
  createdOn?: string | null;
  jsCreatedOn?: Date | null;
  updatedOn?: string | null;
}): number {
  return ticketLatestActivityMsForClientRead(ticket);
}

/** Persist that the logged-in contact has read this thread (survives refresh). */
export function markClientTicketRead(
  contactId: number,
  ticketId: string,
  activityMs: number,
): void {
  const id = String(ticketId ?? "").trim();
  if (contactId <= 0 || !id) {
    return;
  }
  const ms = Number(activityMs);
  if (!Number.isFinite(ms) || ms <= 0) {
    return;
  }
  const map = readMap(contactId);
  map[id] = Math.max(map[id] ?? 0, ms);
  writeMap(contactId, map);
}

/**
 * True when the contact has opened the thread and no newer activity has arrived since.
 * `currentActivityMs` should match `ticketActivityMsForClientRead` on the incoming payload.
 */
export function isTicketReadByClient(
  contactId: number,
  ticketId: string,
  currentActivityMs: number,
): boolean {
  const id = String(ticketId ?? "").trim();
  if (contactId <= 0 || !id) {
    return false;
  }
  const readAt = readMap(contactId)[id];
  if (readAt == null || !Number.isFinite(readAt)) {
    return false;
  }
  const activity = Number(currentActivityMs ?? 0);
  if (!Number.isFinite(activity) || activity <= 0) {
    return true;
  }
  return activity <= readAt;
}
